import { useRef, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { DollarSign, TrendingUp, Cpu, Image as ImageIcon, Search, AlertTriangle, BarChart3, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { useCostSummary, useUsageCosts, useCostsByModel, useCostsDiario, useUpdateBudget } from '../hooks/useCosts'
import { getErrorMessage } from '../lib/errors'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { ProgressBar } from '../components/ui/ProgressBar'
import { QueryErrorState } from '../components/ui/QueryErrorState'
import { formatDistanceToNow, format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { useAppStore } from '../stores/app'

const SUMMARY_PERIOD_OPTIONS = [7, 14, 30] as const
const USAGE_PERIOD_OPTIONS = [7, 14, 30] as const

export function CostsPage() {
  const organizationId = useAppStore((state) => state.organizationId)
  const [summaryDays, setSummaryDays] = useState<number>(30)
  const [usageDays, setUsageDays] = useState<number>(7)

  const { data: summary, isLoading: summaryLoading, isError: summaryError, error: summaryErrorObj, refetch: refetchSummary, isRefetching: summaryRefetching } = useCostSummary(summaryDays)
  const { data: recentUsage, isLoading: usageLoading } = useUsageCosts(usageDays, 50)
  const { data: byModel, isLoading: modelLoading } = useCostsByModel(summaryDays)
  const { data: dailyCosts, isLoading: dailyLoading } = useCostsDiario(summaryDays)
  const updateBudget = useUpdateBudget()
  const [budgetInput, setBudgetInput] = useState('')
  const initialSynced = useRef(false)

  useEffect(() => {
    if (summary != null && !initialSynced.current) {
      initialSynced.current = true
      setBudgetInput(String(summary.budget_limit))
    }
  }, [summary])

  const handleSaveBudget = async () => {
    const parsed = Number(budgetInput)
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error('El presupuesto debe ser un número mayor o igual a 0')
      return
    }
    try {
      await updateBudget.mutateAsync(parsed)
      setBudgetInput(String(parsed))
      toast.success('Presupuesto actualizado correctamente')
    } catch (e) {
      toast.error('Error al actualizar el presupuesto', {
        description: getErrorMessage(e),
      })
    }
  }

  const getModelTypeIcon = (type: string) => {
    switch (type) {
      case 'llm':
        return <Cpu className="w-4 h-4" />
      case 'image':
        return <ImageIcon className="w-4 h-4" />
      case 'research':
        return <Search className="w-4 h-4" />
      default:
        return <DollarSign className="w-4 h-4" />
    }
  }

  const getModelTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      llm: 'LLM',
      image: 'Imagen',
      research: 'Research',
    }
    return labels[type] || type
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Costos</h1>
          <p className="text-slate-500 mt-1">Monitorea el uso de APIs y costos</p>
        </div>
        {organizationId && summary && (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label htmlFor="summary-period" className="text-sm font-medium text-slate-600">
                Resumen y gráfica
              </label>
              <select
                id="summary-period"
                value={summaryDays}
                onChange={(e) => setSummaryDays(Number(e.target.value))}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                {SUMMARY_PERIOD_OPTIONS.map((d) => (
                  <option key={d} value={d}>{d} días</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="usage-period" className="text-sm font-medium text-slate-600">
                Uso reciente
              </label>
              <select
                id="usage-period"
                value={usageDays}
                onChange={(e) => setUsageDays(Number(e.target.value))}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                {USAGE_PERIOD_OPTIONS.map((d) => (
                  <option key={d} value={d}>{d} días</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {!organizationId ? (
        <div className="card py-12 text-center">
          <p className="text-slate-600">No hay organización asignada.</p>
          <p className="text-sm text-slate-500 mt-1">Asigna una organización a tu perfil para ver los costos.</p>
        </div>
      ) : summaryLoading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : summaryError && summaryErrorObj ? (
        <QueryErrorState
          title="Error al cargar los costos"
          error={summaryErrorObj}
          onRetry={() => refetchSummary()}
          isRefetching={summaryRefetching}
        />
      ) : !summary ? (
        <div className="card py-12 text-center">
          <p className="text-slate-600">No hay datos de costos para el período.</p>
          <p className="text-sm text-slate-500 mt-1">Los costos aparecerán cuando se registre uso.</p>
        </div>
      ) : (
        <>
          {/* Resumen de Costos */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="card">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-2xl font-bold text-slate-800">
                    ${summary.total_cost.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Cpu className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">LLM</p>
                  <p className="text-2xl font-bold text-slate-800">
                    ${summary.llm_cost.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <ImageIcon className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Imágenes</p>
                  <p className="text-2xl font-bold text-slate-800">
                    ${summary.image_cost.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-amber-50 rounded-lg">
                  <Search className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Research</p>
                  <p className="text-2xl font-bold text-slate-800">
                    ${summary.research_cost.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Budget Progress */}
          <div className="card mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Presupuesto Mensual</h2>
                <p className="text-sm text-slate-500 mt-1">
                  ${summary.total_cost.toFixed(2)} de ${summary.budget_limit} usado
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <TrendingUp className={`w-4 h-4 ${
                  summary.budget_used_percentage >= 90 ? 'text-red-600' :
                  summary.budget_used_percentage >= 75 ? 'text-amber-600' :
                  'text-emerald-600'
                }`} />
                <span className={`font-medium ${
                  summary.budget_used_percentage >= 90 ? 'text-red-600' :
                  summary.budget_used_percentage >= 75 ? 'text-amber-600' :
                  'text-emerald-600'
                }`}>
                  {summary.budget_used_percentage.toFixed(1)}%
                </span>
              </div>
            </div>
            <ProgressBar 
              value={summary.total_cost} 
              max={summary.budget_limit}
              showPercentage={false}
            />
            {summary.budget_used_percentage >= 75 && (
              <div className={`mt-3 p-3 rounded-lg flex items-start gap-2 ${
                summary.budget_used_percentage >= 90 
                  ? 'bg-red-50 text-red-700' 
                  : 'bg-amber-50 text-amber-700'
              }`}>
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <p className="text-sm">
                  {summary.budget_used_percentage >= 90
                    ? 'Advertencia: Has superado el 90% de tu presupuesto mensual'
                    : 'Atención: Has alcanzado el 75% de tu presupuesto mensual'}
                </p>
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-slate-200 flex flex-wrap items-center gap-3">
              <label htmlFor="budget-limit" className="text-sm font-medium text-slate-700">
                Nuevo límite (USD)
              </label>
              <input
                id="budget-limit"
                type="number"
                min={0}
                step={0.01}
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                className="w-28 px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                disabled={updateBudget.isPending}
              />
              <button
                type="button"
                onClick={handleSaveBudget}
                disabled={updateBudget.isPending}
                className="inline-flex items-center px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateBudget.isPending ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>

          {/* Costos diarios (gráfica) */}
          <div className="card mb-8">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-slate-600" />
              <h2 className="text-lg font-semibold text-slate-800">Costos diarios</h2>
            </div>
            {dailyLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : dailyCosts && dailyCosts.length > 0 ? (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dailyCosts.map((d) => ({
                      ...d,
                      dateLabel: format(parseISO(d.fecha), 'd/M', { locale: es }),
                    }))}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="dateLabel"
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickLine={false}
                      tickFormatter={(v) => `$${v.toFixed(2)}`}
                    />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                      labelFormatter={(_, payload) =>
                        payload[0]?.payload?.fecha
                          ? format(parseISO(payload[0].payload.fecha), "d 'de' MMMM yyyy", { locale: es })
                          : ''
                      }
                      formatter={(value: number | undefined) => [`$${Number(value ?? 0).toFixed(4)}`, 'Costo']}
                    />
                    <Bar dataKey="costo" fill="#0f766e" radius={[4, 4, 0, 0]} name="Costo" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-center py-12 text-slate-400">No hay datos de costos diarios para el período</p>
            )}
          </div>

          {/* Costos por Modelo */}
          <div className="card mb-8">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Costos por Modelo</h2>
            {modelLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : byModel && byModel.length > 0 ? (
              <div className="space-y-3">
                {byModel.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{item.model}</p>
                      <p className="text-sm text-slate-500">{item.count} llamadas</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-800">${item.cost.toFixed(4)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-8 text-slate-400">No hay datos de costos por modelo</p>
            )}
          </div>

          {/* Uso Reciente */}
          <div className="card">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Uso Reciente</h2>
            {usageLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : recentUsage && recentUsage.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Tipo</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Modelo</th>
                      <th className="text-right py-3 px-2 text-sm font-medium text-slate-600">Tokens In</th>
                      <th className="text-right py-3 px-2 text-sm font-medium text-slate-600">Tokens Out</th>
                      <th className="text-right py-3 px-2 text-sm font-medium text-slate-600">Costo</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Tarea</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentUsage.map((usage) => (
                      <tr key={usage.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            {getModelTypeIcon(usage.model_type)}
                            <span className="text-sm text-slate-700">
                              {getModelTypeLabel(usage.model_type)}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-sm text-slate-700">{usage.model}</td>
                        <td className="py-3 px-2 text-sm text-slate-700 text-right">
                          {usage.tokens_input?.toLocaleString() || '-'}
                        </td>
                        <td className="py-3 px-2 text-sm text-slate-700 text-right">
                          {usage.tokens_output?.toLocaleString() || '-'}
                        </td>
                        <td className="py-3 px-2 text-sm font-medium text-slate-800 text-right">
                          ${Number(usage.cost).toFixed(4)}
                        </td>
                        <td className="py-3 px-2 text-sm">
                          {usage.task_id ? (
                            <Link
                              to={`/content/${usage.task_id}`}
                              className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium"
                            >
                              Ver tarea
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Link>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-sm text-slate-500">
                          {formatDistanceToNow(new Date(usage.created_at), {
                            addSuffix: true,
                            locale: es,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center py-8 text-slate-400">No hay registros de uso reciente</p>
            )}
          </div>
        </>
      )}
    </div>
  )
}
